// v1.9.0 · CRM · Fase 3: Calendario (agenda semanal/diaria/lista), citas, horarios bloqueados y configuración de
// calendarios con horario semanal, reparto entre miembros y página pública de reservas.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Lock, Settings2, Copy, Trash2, Mail, Video, CalendarCheck } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';   // v1.13.0 · vuelta de Google (?google=ok|error)
import { api, errMsg, Modal, inputCls, btn, Field, OwnOwner, Empty } from './ui';
import { ContactPicker } from './Opportunities';

export const BIZ_TZ = 'America/Guayaquil';
export const APPT_STATUS = {
  confirmed: { label: 'Confirmada', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  showed: { label: 'Asistió', cls: 'bg-green-50 text-green-700 border-green-200' },
  noshow: { label: 'No asistió', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  cancelled: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-500 border-gray-300' },
};
const LOCATION_TYPES = [['video', 'Videollamada'], ['telefono', 'Llamada telefónica'], ['presencial', 'Presencial'], ['otro', 'Otro']];
const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const DAYS_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const HOUR_PX = 44;

// ── fechas en la hora de Ecuador (independiente de la zona del navegador) ──
const fmtParts = new Intl.DateTimeFormat('en-CA', { timeZone: BIZ_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
export const bizParts = (iso) => {
  const p = Object.fromEntries(fmtParts.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: Number(p.hour) * 60 + Number(p.minute), hhmm: `${p.hour}:${p.minute}` };
};
export const bizToday = () => bizParts(new Date().toISOString()).date;
const addDays = (ymd, n) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const weekday = (ymd) => (new Date(`${ymd}T12:00:00Z`).getUTCDay() + 6) % 7;   // 0 = lunes
const mondayOf = (ymd) => addDays(ymd, -weekday(ymd));
export const dayLabel = (ymd) => { const [, m, d] = ymd.split('-'); return `${DAYS_SHORT[weekday(ymd)]} ${Number(d)} ${MONTHS[Number(m) - 1]}`; };
const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
export const apptWhen = (a) => { const s = bizParts(a.start); const e = bizParts(a.end); return `${dayLabel(s.date)} · ${s.hhmm}–${e.hhmm}`; };

export function ApptBadge({ status }) {
  const s = APPT_STATUS[status] || APPT_STATUS.confirmed;
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

// ───────────────────────────── Cita: alta y edición ─────────────────────────────
export function AppointmentForm({ meta, calendars, appt, preset = {}, onClose, onSaved }) {
  const isNew = !appt?.id;
  const [a, setA] = useState(() => {
    const p = appt ? bizParts(appt.start) : null;
    return {
      calendar_id: appt?.calendar_id || preset.calendar_id || calendars[0]?.id || '',
      contact_id: appt?.contact_id ?? preset.contact?.id ?? null, contact_name: appt?.contact_name ?? preset.contact?.name ?? '',
      contact_email: preset.contact?.email || '',
      date: p?.date || preset.date || bizToday(), time: p?.hhmm || preset.time || '',
      duration: appt ? Math.round((new Date(appt.end) - new Date(appt.start)) / 60000) : null,
      title: appt?.title || '', assigned_to: appt?.assigned_to || '', status: appt?.status || 'confirmed',
      location: appt?.location ?? null, notes: appt?.notes || '', send_confirmation: false,
    };
  });
  const [slots, setSlots] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  const cal = calendars.find((c) => c.id === a.calendar_id);
  const duration = a.duration || cal?.duration || 30;
  const users = meta.users.filter((u) => !cal?.members?.length || cal.members.includes(u.id));
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  useEffect(() => {
    if (!a.calendar_id || !a.date) return undefined;
    let alive = true;
    api.get(`/calendars/${a.calendar_id}/slots`, { date_from: a.date, date_to: a.date })
      .then((r) => { if (alive) setSlots(r.slots); }).catch(() => { if (alive) setSlots([]); });
    return () => { alive = false; };
  }, [a.calendar_id, a.date]);

  const save = async (force = false) => {
    if (!a.time) { toast.error('Elija la hora'); return; }
    setBusy(true); setConflict(null);
    const start = `${a.date}T${a.time}:00-05:00`;
    const startMs = new Date(start).getTime();
    const end = new Date(startMs + duration * 60000).toISOString();
    const body = { title: a.title, start, end, assigned_to: a.assigned_to || null, notes: a.notes, force,
                   ...(a.location !== null ? { location: a.location } : {}) };
    try {
      const r = isNew
        ? await api.post('/appointments', { ...body, calendar_id: a.calendar_id, contact_id: a.contact_id, status: 'confirmed', send_confirmation: a.send_confirmation })
        : await api.put(`/appointments/${appt.id}`, { ...body, status: a.status, contact_id: a.contact_id });
      toast.success(isNew ? 'Cita agendada' : 'Cita actualizada');
      if (r.confirmation && !r.confirmation.sent) toast.error(`La confirmación no se envió: ${r.confirmation.error}`);
      onSaved(r);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.message) setConflict(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('¿Eliminar esta cita? Si solo no se realizará, mejor márquela como «Cancelada».')) return;
    try { await api.del(`/appointments/${appt.id}`); toast.success('Cita eliminada'); onSaved(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const [ga, setGa] = useState(appt || {});   // v1.26.0 · estado de Google de la cita (se actualiza al reintentar)
  const [gBusy, setGBusy] = useState(false);
  const retryGoogle = async () => {
    setGBusy(true);
    try {
      const r = await api.post(`/google/sync/${appt.id}`, {}); setGa(r);
      if (r.google_sync?.ok) toast.success(r.meet_url ? 'En Google Calendar con enlace de Meet' : 'En Google Calendar');
      else toast.error(r.google_sync?.error || 'No se pudo copiar a Google');
    } catch (e) { toast.error(errMsg(e)); } finally { setGBusy(false); }
  };
  const resend = async () => {
    try { const r = await api.post(`/appointments/${appt.id}/confirmation`, {}); if (r.sent) toast.success('Confirmación enviada'); else toast.error(`No se envió: ${r.error}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Modal title={isNew ? 'Agendar cita' : 'Editar cita'} onClose={onClose} wide
           footer={<>
             {!isNew && <button className={`${btn.danger} mr-auto`} onClick={remove}><Trash2 size={14} /> Eliminar</button>}
             {!isNew && appt.contact_id && <button className={btn.ghost} onClick={resend}><Mail size={14} /> Reenviar confirmación</button>}
             <button className={btn.ghost} onClick={onClose}>Cancelar</button>
             <button className={btn.primary} disabled={busy || !a.calendar_id || !a.time} onClick={() => save(false)} data-testid="appt-save">{busy ? 'Guardando…' : 'Guardar'}</button>
           </>}>
      {conflict && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <b>{conflict.message}</b>
          {conflict.conflicts?.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs">{conflict.conflicts.map((c, i) => <li key={i}>{c.type === 'bloqueo' ? 'Bloqueo' : c.type === 'google' ? 'Google' : 'Cita'}: {c.title} · {apptWhen(c)}</li>)}</ul>
          )}
          <button className="mt-1.5 text-xs font-semibold underline" onClick={() => save(true)}>Agendar de todas formas</button>
        </div>
      )}
      {!isNew && (ga.meet_url || ga.google_sync || ga.status !== 'cancelled') && (   // v1.13.0 · v1.26.0 reintentar
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs" data-testid="appt-google">
          {ga.meet_url && <a href={ga.meet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1a73e8] hover:underline"><Video size={14} /> Unirse con Google Meet</a>}
          {ga.google_sync?.ok && ga.google_event_id && <span className="inline-flex items-center gap-1 text-green-700"><CalendarCheck size={14} /> En Google Calendar</span>}
          {ga.google_sync && !ga.google_sync.ok && ga.google_sync.error && <span className="text-amber-800">Google Calendar: {ga.google_sync.error}</span>}
          {!ga.google_event_id && !ga.google_sync?.error && ga.status !== 'cancelled' && <span className="text-gray-500">Esta cita no está en Google Calendar.</span>}
          {ga.status !== 'cancelled' && (!ga.google_event_id || !ga.google_sync?.ok) && (
            <button type="button" className="font-semibold text-[#2F3D8E] underline disabled:opacity-50" onClick={retryGoogle} disabled={gBusy} data-testid="appt-google-retry">
              {gBusy ? 'Copiando…' : 'Reintentar en Google'}</button>
          )}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Calendario">
          <select className={inputCls} value={a.calendar_id} onChange={set('calendar_id')} disabled={!isNew}>
            {calendars.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.duration} min</option>)}
          </select>
        </Field>
        <Field label="Contacto" group>
          <ContactPicker value={a.contact_id} label={a.contact_name} onChange={(id, name) => setA({ ...a, contact_id: id, contact_name: name })} />
        </Field>
        <Field label="Fecha"><input type="date" className={inputCls} value={a.date} onChange={(e) => setA({ ...a, date: e.target.value, time: '' })} /></Field>
        <Field label="Hora" hint={`Duración ${duration} min · hora de Ecuador`}>
          <input type="time" className={inputCls} value={a.time} onChange={set('time')} step={300} data-testid="appt-time" />
        </Field>
        <div className="sm:col-span-2">
          <div className="mb-1 text-[13px] font-semibold text-gray-700">Horarios libres</div>
          {slots.length === 0 ? <p className="text-xs text-gray-500">No hay horarios libres ese día según el horario del calendario. Puede escribir otra hora.</p> : (
            <div className="flex flex-wrap gap-1.5">
              {slots.map((s) => {
                const t = bizParts(s.start).hhmm;
                return <button key={s.start} type="button" onClick={() => setA({ ...a, time: t })} data-testid="appt-slot"
                               className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${a.time === t ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#2F3D8E]'}`}>{t}</button>;
              })}
            </div>
          )}
        </div>
        {meta.scope === 'own' ? <OwnOwner meta={meta} /> : (   // v1.12.1
        <Field label="Responsable">
          <select className={inputCls} value={a.assigned_to} onChange={set('assigned_to')}>
            <option value="">{cal?.members?.length ? 'Automático (quien esté libre)' : 'Sin asignar'}</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        )}
        {!isNew ? (
          <Field label="Estado">
            <select className={inputCls} value={a.status} onChange={set('status')}>
              {Object.entries(APPT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
        ) : <Field label="Título (opcional)"><input className={inputCls} value={a.title} onChange={set('title')} placeholder={cal ? `${cal.name} · contacto` : ''} /></Field>}
        <Field label="Lugar o enlace"><input className={inputCls} value={a.location ?? cal?.location ?? ''} onChange={set('location')} /></Field>
        <div className="sm:col-span-2"><Field label="Notas"><textarea className={inputCls} rows={2} value={a.notes} onChange={set('notes')} /></Field></div>
        {isNew && a.contact_id && (
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={a.send_confirmation} onChange={(e) => setA({ ...a, send_confirmation: e.target.checked })} />
            Enviar confirmación por correo al contacto
          </label>
        )}
      </div>
    </Modal>
  );
}

// ───────────────────────────── Bloquear horario ─────────────────────────────
function BlockForm({ meta, calendars, preset, onClose, onSaved }) {
  const own = meta.scope === 'own';   // v1.12.1 · «solo sus registros»: solo puede bloquear su propio horario
  const [b, setB] = useState({ target: calendars[0] && !own ? `cal:${calendars[0].id}` : `user:${meta.me || ''}`, date: preset?.date || bizToday(),
                               from: preset?.time || '09:00', to: '', reason: '' });
  const save = async () => {
    const [kind, id] = b.target.split(':');
    try {
      await api.post('/blocks', { calendar_id: kind === 'cal' ? id : null, user_id: kind === 'user' ? id : null,
                                  start: `${b.date}T${b.from}:00-05:00`, end: `${b.date}T${b.to}:00-05:00`, reason: b.reason });
      toast.success('Horario bloqueado'); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title="Bloquear horario" onClose={onClose}
           footer={<><button className={btn.ghost} onClick={onClose}>Cancelar</button><button className={btn.primary} disabled={!b.to || !b.from} onClick={save}>Bloquear</button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        {own ? <div className="sm:col-span-2"><OwnOwner meta={meta} label="Para" /></div> : <div className="sm:col-span-2"><Field label="Para">
          <select className={inputCls} value={b.target} onChange={(e) => setB({ ...b, target: e.target.value })}>
            <optgroup label="Calendario (todos sus miembros)">{calendars.map((c) => <option key={c.id} value={`cal:${c.id}`}>{c.name}</option>)}</optgroup>
            <optgroup label="Persona">{meta.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
          </select>
        </Field></div>}
        <Field label="Fecha"><input type="date" className={inputCls} value={b.date} onChange={(e) => setB({ ...b, date: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Desde"><input type="time" className={inputCls} value={b.from} onChange={(e) => setB({ ...b, from: e.target.value })} /></Field>
          <Field label="Hasta"><input type="time" className={inputCls} value={b.to} onChange={(e) => setB({ ...b, to: e.target.value })} /></Field>
        </div>
        <div className="sm:col-span-2"><Field label="Motivo"><input className={inputCls} value={b.reason} onChange={(e) => setB({ ...b, reason: e.target.value })} placeholder="Feriado, capacitación, almuerzo…" /></Field></div>
      </div>
    </Modal>
  );
}

// ───────────────────────────── Configurar calendarios ─────────────────────────────
const NEW_CAL = { name: 'Nuevo calendario', description: '', color: '#2F3D8E', duration: 30, slot_interval: null, buffer_before: 0, buffer_after: 0,
  min_notice_hours: 2, max_days_ahead: 60, members: [], location_type: 'video', location: '', public: false, slug: '', active: true, google_meet: false,
  availability: Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((d) => [String(d), d < 5 ? [['09:00', '13:00'], ['14:00', '18:00']] : []])) };

function CalendarEditor({ meta, value, onSaved, onDeleted }) {
  const [c, setC] = useState(value);
  const num = (k) => (e) => setC({ ...c, [k]: e.target.value === '' ? '' : Number(e.target.value) });
  const setDay = (d, ranges) => setC({ ...c, availability: { ...c.availability, [d]: ranges } });
  const link = c.slug ? `${window.location.origin}/reservar/${c.slug}` : '';
  const save = async () => {
    const body = { ...c, slot_interval: c.slot_interval || null };
    try { const r = c.id ? await api.put(`/calendars/${c.id}`, body) : await api.post('/calendars', body); toast.success('Calendario guardado'); onSaved(r); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar el calendario «${c.name}»?`)) return;
    try { await api.del(`/calendars/${c.id}`); toast.success('Calendario eliminado'); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success('Enlace copiado'); } catch { toast.error('No se pudo copiar'); } };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_90px]">
        <Field label="Nombre"><input className={inputCls} value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></Field>
        <Field label="Color"><input type="color" className="h-[38px] w-full rounded-lg border border-gray-300" value={c.color} onChange={(e) => setC({ ...c, color: e.target.value })} /></Field>
      </div>
      <Field label="Descripción" hint="Se muestra en la página de reservas"><textarea className={inputCls} rows={2} value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Duración (min)"><input type="number" min="5" className={inputCls} value={c.duration} onChange={num('duration')} /></Field>
        <Field label="Cada (min)" hint="Vacío = la duración"><input type="number" min="5" className={inputCls} value={c.slot_interval ?? ''} onChange={num('slot_interval')} /></Field>
        <Field label="Anticipación mín. (h)"><input type="number" min="0" className={inputCls} value={c.min_notice_hours} onChange={num('min_notice_hours')} /></Field>
        <Field label="Margen antes (min)"><input type="number" min="0" className={inputCls} value={c.buffer_before} onChange={num('buffer_before')} /></Field>
        <Field label="Margen después (min)"><input type="number" min="0" className={inputCls} value={c.buffer_after} onChange={num('buffer_after')} /></Field>
        <Field label="Hasta (días)"><input type="number" min="1" className={inputCls} value={c.max_days_ahead} onChange={num('max_days_ahead')} /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Lugar">
          <select className={inputCls} value={c.location_type} onChange={(e) => setC({ ...c, location_type: e.target.value })}>
            {LOCATION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Dirección, teléfono o enlace"><input className={inputCls} value={c.location} onChange={(e) => setC({ ...c, location: e.target.value })} /></Field>
      </div>
      {c.location_type === 'video' && (   // v1.13.0
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!c.google_meet} onChange={(e) => setC({ ...c, google_meet: e.target.checked })} data-testid="cal-google-meet" />
          <span><b>Crear enlace de Google Meet</b> en cada cita. Funciona cuando el responsable tiene Google Calendar conectado; el enlace sale en la confirmación.</span>
        </label>
      )}
      <Field label="Miembros" group hint="Las citas se reparten entre quienes estén libres (el que tenga menos citas ese día). Sin miembros, el calendario tiene una sola agenda.">
        <div className="flex flex-wrap gap-1.5">
          {meta.users.map((u) => {
            const on = c.members.includes(u.id);
            return <button key={u.id} type="button" onClick={() => setC({ ...c, members: on ? c.members.filter((x) => x !== u.id) : [...c.members, u.id] })}
                           className={`rounded-full border px-2.5 py-1 text-xs ${on ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white'}`}>{u.name}</button>;
          })}
        </div>
      </Field>
      <div>
        <div className="mb-1.5 text-[13px] font-semibold text-gray-700">Horario semanal (hora de Ecuador)</div>
        <div className="space-y-1.5">
          {DAYS.map((name, d) => {
            const ranges = c.availability[String(d)] || [];
            return (
              <div key={d} className="flex flex-wrap items-center gap-2 text-sm">
                <label className="flex w-28 items-center gap-2">
                  <input type="checkbox" checked={ranges.length > 0} onChange={(e) => setDay(String(d), e.target.checked ? [['09:00', '18:00']] : [])} /> {name}
                </label>
                {ranges.length === 0 && <span className="text-xs text-gray-400">No disponible</span>}
                {ranges.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <input type="time" className="rounded border border-gray-300 px-1.5 py-1 text-xs" value={r[0]} aria-label={`${name} desde`}
                           onChange={(e) => setDay(String(d), ranges.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />
                    –
                    <input type="time" className="rounded border border-gray-300 px-1.5 py-1 text-xs" value={r[1]} aria-label={`${name} hasta`}
                           onChange={(e) => setDay(String(d), ranges.map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
                    <button type="button" className="px-1 text-gray-400 hover:text-red-600" aria-label="Quitar franja" onClick={() => setDay(String(d), ranges.filter((_, j) => j !== i))}>×</button>
                  </span>
                ))}
                {ranges.length > 0 && <button type="button" className="text-xs font-semibold text-[#2F3D8E]" onClick={() => setDay(String(d), [...ranges, ['15:00', '18:00']])}>+ franja</button>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="space-y-2 rounded-lg border border-gray-200 p-3">
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={c.public} onChange={(e) => setC({ ...c, public: e.target.checked })} /> Página pública de reservas</label>
        <Field label="Enlace" hint="Solo letras, números y guiones"><input className={inputCls} value={c.slug} onChange={(e) => setC({ ...c, slug: e.target.value })} placeholder="se genera con el nombre" /></Field>
        {c.public && c.id && link && (
          <div className="flex items-center gap-2 text-xs">
            <a href={link} target="_blank" rel="noreferrer" className="truncate text-[#2F3D8E] underline">{link}</a>
            <button type="button" className={btn.ghost} onClick={copy}><Copy size={13} /> Copiar</button>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.active} onChange={(e) => setC({ ...c, active: e.target.checked })} /> Activo</label>
      </div>
      <div className="flex gap-2">
        <button className={btn.primary} onClick={save} disabled={!c.name.trim()}>Guardar calendario</button>
        {c.id && <button className={btn.danger} onClick={remove}><Trash2 size={14} /> Eliminar</button>}
      </div>
    </div>
  );
}

function CalendarsManager({ meta, calendars, onClose, onChanged }) {
  const [sel, setSel] = useState(calendars[0]?.id || 'new');
  const current = sel === 'new' ? NEW_CAL : calendars.find((c) => c.id === sel) || NEW_CAL;
  return (
    <Modal title="Calendarios" onClose={onClose} wide>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {calendars.map((c) => (
          <button key={c.id} onClick={() => setSel(c.id)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${sel === c.id ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white'}`}>
            <i className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}
          </button>
        ))}
        <button onClick={() => setSel('new')} className={`rounded-full px-3 py-1 text-xs font-semibold ${sel === 'new' ? 'bg-[#2F3D8E] text-white' : 'border border-dashed border-gray-400 bg-white'}`}>+ Nuevo</button>
      </div>
      <CalendarEditor key={sel === 'new' ? 'new' : `${current.id}:${current.slug}`} meta={meta} value={current}
                      onSaved={(r) => { onChanged(); setSel(r.id); }} onDeleted={() => { onChanged(); setSel('new'); }} />
    </Modal>
  );
}

// ───────────────────────────── Agenda ─────────────────────────────
function lanes(items) {
  // reparte en columnas solo las citas que se cruzan entre sí (v1.9.1 · por grupo, no por día completo)
  const sorted = [...items].sort((x, y) => x.s - y.s || y.e - x.e);
  let group = []; let groupEnd = -1; let ends = [];
  const close = () => { group.forEach((it) => { it.lanes = ends.length; }); group = []; ends = []; };
  sorted.forEach((it) => {
    if (it.s >= groupEnd) { close(); groupEnd = -1; }
    let lane = ends.findIndex((e) => e <= it.s);
    if (lane === -1) { lane = ends.length; ends.push(it.e); } else ends[lane] = it.e;
    it.lane = lane;
    group.push(it);
    groupEnd = Math.max(groupEnd, it.e);
  });
  close();
  return sorted;
}

function WeekGrid({ days, appts, blocks, users, onNew, onOpen }) {
  const all = [...appts.map((a) => ({ ...bizParts(a.start), endMin: bizParts(a.end).minutes })), ...blocks.map((b) => ({ ...bizParts(b.start), endMin: bizParts(b.end).minutes }))];
  const minH = Math.min(8, ...all.map((x) => Math.floor(x.minutes / 60)));
  const maxH = Math.max(19, ...all.map((x) => Math.ceil((x.endMin || 1440) / 60)));
  const hours = Array.from({ length: maxH - minH }, (_, i) => minH + i);
  const today = bizToday();
  const top = (m) => ((m - minH * 60) / 60) * HOUR_PX;
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <div className="grid" style={{ gridTemplateColumns: `52px repeat(${days.length}, minmax(${days.length > 1 ? 120 : 220}px, 1fr))` }}>
        <div className="border-b" />
        {days.map((d) => (
          <div key={d} className={`border-b border-l px-2 py-2 text-center text-xs font-semibold ${d === today ? 'text-[#2F3D8E]' : 'text-gray-600'}`}>
            {dayLabel(d)}{d === today && <span className="ml-1 rounded bg-[#2F3D8E] px-1 text-[10px] text-white">hoy</span>}
          </div>
        ))}
        <div className="relative">
          {hours.map((h) => <div key={h} className="pr-1.5 text-right text-[10px] text-gray-400" style={{ height: HOUR_PX }}>{hhmm(h * 60)}</div>)}
        </div>
        {days.map((d) => {
          const dayAppts = lanes(appts.filter((a) => bizParts(a.start).date === d).map((a) => ({ a, s: bizParts(a.start).minutes, e: bizParts(a.end).date === d ? bizParts(a.end).minutes : 1440 })));
          const dayBlocks = blocks.filter((b) => bizParts(b.start).date === d);
          return (
            <div key={d} className="relative border-l" style={{ height: hours.length * HOUR_PX }} data-testid="cal-day" data-day={d}>
              {hours.map((h) => (
                <button key={h} type="button" aria-label={`Nueva cita ${dayLabel(d)} ${hhmm(h * 60)}`} onClick={() => onNew(d, hhmm(h * 60))}
                        className="block w-full border-b border-gray-100 hover:bg-[#2F3D8E]/5" style={{ height: HOUR_PX }} />
              ))}
              {dayBlocks.map((b) => {
                const s = bizParts(b.start).minutes; const e = bizParts(b.end).date === d ? bizParts(b.end).minutes : 1440;
                return (
                  <div key={b.id} className="pointer-events-none absolute inset-x-0.5 overflow-hidden rounded bg-[repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb_6px,#f3f4f6_6px,#f3f4f6_12px)] px-1.5 py-0.5 text-[10px] text-gray-600"
                       style={{ top: top(s), height: Math.max(14, top(e) - top(s)) }}>
                    <Lock size={9} className="mr-0.5 inline" />{b.reason || 'Bloqueado'}
                  </div>
                );
              })}
              {dayAppts.map(({ a, s, e, lane, lanes: n }) => (
                <button key={a.id} type="button" onClick={() => onOpen(a)} data-testid="cal-appt"
                        className={`absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm hover:z-10 hover:shadow ${a.status === 'cancelled' ? 'bg-gray-100 text-gray-400 line-through' : 'bg-white text-gray-800'}`}
                        style={{ top: top(s) + 1, height: Math.max(18, top(e) - top(s) - 2), left: `calc(${(lane / n) * 100}% + 2px)`, width: `calc(${100 / n}% - 4px)`, borderLeftColor: a.calendar_color || '#2F3D8E', background: a.status === 'cancelled' ? undefined : `${a.calendar_color || '#2F3D8E'}14` }}>
                  <b className="block truncate">{hhmm(s)} {a.contact_name || a.title}</b>
                  <span className="block truncate text-gray-500">{a.calendar_name}{users[a.assigned_to] ? ` · ${users[a.assigned_to]}` : ''}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────── v1.13.0 · Conexión con Google Calendar (de cada persona) ─────────────────────────────
export function GoogleConnect() {
  const [g, setG] = useState(null);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = useCallback(async () => { try { setG(await api.get('/google/me')); } catch { setG(null); } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const r = params.get('google');
    if (!r) return;
    if (r === 'ok') toast.success('Google Calendar conectado'); else toast.error(params.get('msg') || 'No se pudo conectar Google');
    setParams({ tab: 'calendar' });
    load();
  }, [params, setParams, load]);
  if (!g || !g.configured) return null;
  const connect = async () => { try { window.location.href = (await api.post('/google/connect', {})).url; } catch (e) { toast.error(errMsg(e)); } };
  const disconnect = async () => {
    if (!window.confirm('¿Desconectar su Google Calendar? Las citas nuevas ya no se copiarán a Google.')) return;
    try { await api.del('/google/me'); setOpen(false); load(); toast.success('Google desconectado'); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleBusy = async () => { try { setG(await api.put('/google/me', { check_busy: !g.check_busy })); } catch (e) { toast.error(errMsg(e)); } };
  if (!g.connected) return <button className={btn.ghost} onClick={connect} data-testid="google-connect"><CalendarCheck size={14} /> Conectar Google Calendar</button>;
  const warn = g.status === 'reconnect';
  return (
    <div className="relative">
      <button className={`${btn.ghost} ${warn ? 'border-amber-300 text-amber-800' : ''}`} onClick={() => (warn ? connect() : setOpen((v) => !v))} aria-expanded={open} data-testid="google-status">
        <CalendarCheck size={14} className={warn ? '' : 'text-green-600'} /> {warn ? 'Reconectar Google' : <span className="max-w-[160px] truncate">{g.email || 'Google conectado'}</span>}
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-xl sm:left-auto sm:right-0 border border-gray-200 bg-white p-3 text-sm shadow-lg" data-testid="google-menu">
          <p className="font-semibold text-[#0F2A3A]">Google Calendar conectado</p>
          <p className="truncate text-xs text-gray-500">{g.email}</p>
          <p className="mt-2 text-xs text-gray-600">Sus citas del CRM se copian a su Google Calendar{g.check_busy ? ' y lo que tiene ocupado allá bloquea esas horas en las reservas' : ''}.</p>
          <label className="mt-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={g.check_busy} onChange={toggleBusy} /> Usar mi ocupado de Google al agendar</label>
          {g.last_error && <p className="mt-2 text-xs text-amber-800">Último aviso: {g.last_error}</p>}
          <button className="mt-3 text-xs font-semibold text-red-600 hover:underline" onClick={disconnect}>Desconectar</button>
        </div>
      )}
    </div>
  );
}

export default function CrmCalendar({ meta }) {
  const [calendars, setCalendars] = useState(null);
  const [view, setView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'day' : 'week'));
  const [anchor, setAnchor] = useState(bizToday());
  const [calFilter, setCalFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [appts, setAppts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [modal, setModal] = useState(null);
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);

  const days = useMemo(() => {
    if (view === 'day') return [anchor];
    if (view === 'week') { const m = mondayOf(anchor); return Array.from({ length: 7 }, (_, i) => addDays(m, i)); }
    return Array.from({ length: 30 }, (_, i) => addDays(anchor, i));
  }, [view, anchor]);

  const loadCalendars = useCallback(async () => { try { setCalendars((await api.get('/calendars')).items); } catch (e) { toast.error(errMsg(e)); setCalendars([]); } }, []);
  useEffect(() => { loadCalendars(); }, [loadCalendars]);
  const load = useCallback(async () => {
    const from = days[0]; const to = days[days.length - 1];
    try {
      const [a, b] = await Promise.all([
        api.get('/appointments', { date_from: from, date_to: to, calendar_id: calFilter, assigned_to: userFilter }),
        api.get('/blocks', { date_from: from, date_to: to }),
      ]);
      setAppts(a.items); setBlocks(b.items);
    } catch (e) { toast.error(errMsg(e)); }
  }, [days, calFilter, userFilter]);
  useEffect(() => { load(); }, [load]);

  const shownBlocks = blocks.filter((b) => (!calFilter || b.calendar_id === calFilter || (b.user_id && !b.calendar_id)) && (!userFilter || b.user_id === userFilter || b.calendar_id));
  const move = (n) => setAnchor(addDays(anchor, view === 'day' ? n : view === 'week' ? 7 * n : 30 * n));
  const label = view === 'day' ? dayLabel(anchor) : `${dayLabel(days[0])} – ${dayLabel(days[days.length - 1])}`;
  const delBlock = async (b) => {
    if (!window.confirm('¿Quitar este bloqueo?')) return;
    try { await api.del(`/blocks/${b.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!calendars) return <div className="py-16 text-center text-sm text-gray-500">Cargando calendario…</div>;
  if (calendars.length === 0) {
    return (
      <>
        <div className="mb-3 flex justify-end"><GoogleConnect /></div>{/* v1.14.1 · también sin calendarios */}
        <Empty title="Aún no hay calendarios" action={meta.is_admin ? <button className={btn.primary} onClick={() => setModal({ t: 'manage' })}><Plus size={14} /> Crear calendario</button> : null}>
          {meta.is_admin ? 'Cree un calendario con su horario de atención para agendar citas y publicar su página de reservas.' : 'Pida a un administrador del CRM que cree el primer calendario.'}
        </Empty>
        {modal?.t === 'manage' && <CalendarsManager meta={meta} calendars={calendars} onClose={() => setModal(null)} onChanged={loadCalendars} />}
      </>
    );
  }
  const byDay = days.map((d) => ({ d, items: appts.filter((a) => bizParts(a.start).date === d) })).filter((x) => x.items.length);

  return (
    <div className="space-y-3" data-testid="crm-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button className={btn.ghost} onClick={() => move(-1)} aria-label="Anterior"><ChevronLeft size={15} /></button>
          <button className={btn.ghost} onClick={() => setAnchor(bizToday())}>Hoy</button>
          <button className={btn.ghost} onClick={() => move(1)} aria-label="Siguiente"><ChevronRight size={15} /></button>
        </div>
        <div className="min-w-[150px] text-sm font-bold text-[#0F2A3A]">{label}</div>
        <div className="flex overflow-hidden rounded-lg border border-gray-300">
          {[['day', 'Día'], ['week', 'Semana'], ['list', 'Lista']].map(([v, l]) => (
            <button key={v} className={`px-3 py-1.5 text-xs font-semibold ${view === v ? 'bg-[#2F3D8E] text-white' : 'bg-white text-gray-600'}`} onClick={() => setView(v)}>{l}</button>
          ))}
        </div>
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm" value={calFilter} onChange={(e) => setCalFilter(e.target.value)} aria-label="Calendario">
          <option value="">Todos los calendarios</option>{calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} aria-label="Responsable">
          <option value="">Todo el equipo</option>{meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <div className="ml-auto flex flex-wrap gap-2">
          <GoogleConnect />
          <button className={btn.ghost} onClick={() => setModal({ t: 'block' })}><Lock size={14} /> Bloquear</button>
          {meta.is_admin && <button className={btn.ghost} onClick={() => setModal({ t: 'manage' })}><Settings2 size={14} /> Calendarios</button>}
          <button className={btn.primary} onClick={() => setModal({ t: 'appt', preset: { date: view === 'week' ? bizToday() : anchor, calendar_id: calFilter } })} data-testid="cal-new"><Plus size={14} /> Cita</button>
        </div>
      </div>

      {view === 'list' ? (
        byDay.length === 0 ? <Empty title="Sin citas en los próximos 30 días">Agende una con «+ Cita» o comparta la página de reservas.</Empty> : (
          <div className="space-y-3">
            {byDay.map(({ d, items }) => (
              <section key={d} className="rounded-xl border border-gray-200 bg-white">
                <h3 className="border-b px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-500">{dayLabel(d)}</h3>
                <ul className="divide-y">
                  {items.map((a) => (
                    <li key={a.id}>
                      <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left text-sm hover:bg-gray-50" onClick={() => setModal({ t: 'appt', appt: a })}>
                        <span className="w-24 font-semibold">{bizParts(a.start).hhmm}–{bizParts(a.end).hhmm}</span>
                        <i className="h-2.5 w-2.5 rounded-full" style={{ background: a.calendar_color }} />
                        <span className="min-w-0 flex-1 truncate">{a.contact_name || a.title} <span className="text-gray-500">· {a.calendar_name}</span></span>
                        <span className="text-xs text-gray-500">{users[a.assigned_to] || 'Sin asignar'}</span>
                        <ApptBadge status={a.status} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )
      ) : (
        <WeekGrid days={days} appts={appts} blocks={shownBlocks} users={users}
                  onNew={(d, t) => setModal({ t: 'appt', preset: { date: d, time: t, calendar_id: calFilter } })} onOpen={(a) => setModal({ t: 'appt', appt: a })} />
      )}

      {shownBlocks.length > 0 && view !== 'list' && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="text-gray-500">Bloqueos:</span>
          {shownBlocks.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
              {apptWhen(b)} · {b.reason || 'Bloqueado'}{b.user_id ? ` · ${users[b.user_id] || ''}` : ''}
              {(meta.is_admin || b.user_id === meta.me || b.created_by === meta.me_name) && (   // v1.9.1 · solo quien puede quitarlo
                <button onClick={() => delBlock(b)} aria-label="Quitar bloqueo" className="text-gray-400 hover:text-red-600">×</button>
              )}
            </span>
          ))}
        </div>
      )}

      {modal?.t === 'appt' && <AppointmentForm meta={meta} calendars={calendars} appt={modal.appt} preset={modal.preset} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.t === 'block' && <BlockForm meta={meta} calendars={calendars} preset={modal.preset} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.t === 'manage' && <CalendarsManager meta={meta} calendars={calendars} onClose={() => { setModal(null); load(); }} onChanged={loadCalendars} />}
      <p className="text-[11px] text-gray-400">Horas en la zona de Ecuador.</p>
    </div>
  );
}
